import { AIModel, AI_MODELS, FORBIDDEN_WORDS } from '@/constants/models';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

const MIN_PROMPT_LENGTH = 3;
const MIN_PASSWORD_LENGTH = 6;
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const forbiddenRegex = /[!@#$%^&*()]/;
const specialCharsRegex = /[!@#$%^&*()]/g;

export const validatePrompt = (prompt: string, bypassForbiddenWords: boolean = false): ValidationResult => {
  const errors: string[] = [];
  const trimmedPrompt = prompt.trim();
  
  if (!trimmedPrompt) {
    errors.push('Prompt cannot be empty');
  } else if (trimmedPrompt.length < MIN_PROMPT_LENGTH) {
    errors.push(`Prompt must be at least ${MIN_PROMPT_LENGTH} characters long`);
  } else {
    // Check if prompt contains special characters
    // Only reject if the prompt is ONLY special characters or has no meaningful content
    // eslint-disable-next-line unicorn/prefer-string-replace-all
    const promptWithoutSpecialChars = trimmedPrompt.replace(specialCharsRegex, '').trim();
    
    // If after removing special characters, there's no meaningful content (less than 3 chars), reject it
    if (forbiddenRegex.test(trimmedPrompt) && promptWithoutSpecialChars.length < MIN_PROMPT_LENGTH) {
      errors.push("Prompt should not contain only special characters. Please include meaningful text along with any special characters.");
    }
  }

  // Check for forbidden words only if not bypassing
  // Use whole-word matching to avoid false positives (e.g., "cute" shouldn't match "cut")
  if (!bypassForbiddenWords) {
    const lowerCasePrompt = trimmedPrompt.toLowerCase();
    const foundForbiddenWords = FORBIDDEN_WORDS.filter(word => {
      // Use word boundary regex to match only complete words
      // \b ensures we match the word as a standalone term, not as part of another word
      const wordBoundaryPattern = new RegExp(String.raw`\b${word.toLowerCase()}\b`, 'i');
      return wordBoundaryPattern.test(lowerCasePrompt);
    });
    
    if (foundForbiddenWords.length > 0) {
      const wordsDisplay = foundForbiddenWords.map(word => `"${word}"`).join(', ');
      errors.push(`Your prompt contains forbidden words (${wordsDisplay}) that could prevent video generation. You can modify it for improved results, or click "Generate" again to continue with generation.`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateModel = (model: string): ValidationResult => {
  const errors: string[] = [];
  
  if (!model) {
    errors.push('Model selection is required');
  }
  
  if (!AI_MODELS.includes(model as AIModel)) {
    errors.push('Invalid model selected');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateVideoGeneration = (prompt: string, model: string, bypassForbiddenWords: boolean = false): ValidationResult => {
  const promptValidation = validatePrompt(prompt, bypassForbiddenWords);
  const modelValidation = validateModel(model);
  
  return {
    isValid: promptValidation.isValid && modelValidation.isValid,
    errors: [...promptValidation.errors, ...modelValidation.errors]
  };
};

export const validateEmail = (email: string): ValidationResult => {
  const errors: string[] = [];
  const trimmedEmail = email.trim();
  
  if (!trimmedEmail) {
    errors.push('Email is required');
  } else if (!EMAIL_REGEX.test(trimmedEmail)) {
    errors.push('Please enter a valid email address');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validatePassword = (password: string): ValidationResult => {
  const errors: string[] = [];
  
  if (!password) {
    errors.push('Password is required');
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
